import React, {useMemo, useState} from 'react';
import {isDevelopmentEnvironment} from 'utils/environment';

function buildTimeline(terms, masteryQuestionCount) {
  const nodes = [
    {
      id: 'tutorial',
      label: 'Tutorial',
      shortLabel: 'TUT',
      phase: 'tutorial',
      termIndex: 0,
      masteryQuestionIndex: 0,
    },
    {
      id: 'frontloading',
      label: 'Frontloading',
      shortLabel: 'FL',
      phase: 'frontloading',
      termIndex: 0,
      masteryQuestionIndex: 0,
    },
  ];

  terms.forEach((term, index) => {
    nodes.push({
      id: `term-${index + 1}-learn`,
      label: `${term.term} - Learn`,
      shortLabel: `VT${index + 1}.L`,
      phase: 'learning',
      termIndex: index,
      masteryQuestionIndex: 0,
    });
    nodes.push({
      id: `term-${index + 1}-work`,
      label: `${term.term} - Work`,
      shortLabel: `VT${index + 1}.W`,
      phase: 'workbook_ready',
      termIndex: index,
      masteryQuestionIndex: 0,
    });
    nodes.push({
      id: `term-${index + 1}-check`,
      label: `${term.term} - Check`,
      shortLabel: `VT${index + 1}.C`,
      phase: 'knowledge_check',
      termIndex: index,
      masteryQuestionIndex: 0,
    });
  });

  for (let index = 0; index < masteryQuestionCount; index += 1) {
    nodes.push({
      id: `mastery-${index + 1}`,
      label: `Mastery Q${index + 1}`,
      shortLabel: `MT.Q${index + 1}`,
      phase: 'mastery',
      termIndex: Math.max(0, terms.length - 1),
      masteryQuestionIndex: index,
    });
  }

  nodes.push({
    id: 'results',
    label: 'Results',
    shortLabel: 'RES',
    phase: 'results',
    termIndex: Math.max(0, terms.length - 1),
    masteryQuestionIndex: 0,
  });

  return nodes;
}

function findCurrentIndex(nodes, phase, activeTermIndex, activeQuestionIndex) {
  if (phase === 'tutorial') {
    return nodes.findIndex(node => node.phase === 'tutorial');
  }
  if (phase === 'frontloading') {
    return nodes.findIndex(node => node.phase === 'frontloading');
  }
  if (phase === 'learning') {
    return nodes.findIndex(
      node => node.phase === 'learning' && node.termIndex === activeTermIndex,
    );
  }
  if (
    ['workbook_ready', 'workbook_exercise', 'workbook_options'].includes(phase)
  ) {
    return nodes.findIndex(
      node => node.phase === 'workbook_ready' && node.termIndex === activeTermIndex,
    );
  }
  if (['knowledge_check', 'knowledge_check_summary'].includes(phase)) {
    return nodes.findIndex(
      node => node.phase === 'knowledge_check' && node.termIndex === activeTermIndex,
    );
  }
  if (phase === 'mastery') {
    return nodes.findIndex(
      node =>
        node.phase === 'mastery' &&
        node.masteryQuestionIndex === activeQuestionIndex,
    );
  }
  if (phase === 'results') {
    return nodes.findIndex(node => node.phase === 'results');
  }
  return 0;
}

const LCVDevStrip = ({
  phase,
  activeTermIndex,
  activeQuestionIndex,
  terms,
  masteryQuestionCount,
  metadata,
  onJump,
}) => {
  const [expanded, setExpanded] = useState(false);
  const timeline = useMemo(
    () => buildTimeline(terms, masteryQuestionCount),
    [terms, masteryQuestionCount],
  );
  const currentIndex = findCurrentIndex(
    timeline,
    phase,
    activeTermIndex,
    activeQuestionIndex,
  );
  const currentNode = timeline[currentIndex];
  const lessonTitle = metadata?.topic || 'Current Lesson';

  if (!isDevelopmentEnvironment) {
    return null;
  }

  const prev = () => {
    if (currentIndex > 0) onJump(timeline[currentIndex - 1]);
  };

  const next = () => {
    if (currentIndex < timeline.length - 1) onJump(timeline[currentIndex + 1]);
  };

  return (
    <div className="lcv-dev-nav" data-expanded={expanded}>
      <button
        type="button"
        className="lcv-dev-toggle"
        onClick={() => setExpanded(current => !current)}
      >
        {expanded ? '▼' : '▲'} DEV
      </button>

      {expanded && (
        <div className="lcv-dev-body">
          <div className="lcv-dev-arrows">
            <button
              type="button"
              className="lcv-dev-arr"
              onClick={prev}
              disabled={currentIndex <= 0}
            >
              ◀ Prev
            </button>
            <span className="lcv-dev-arr-current">
              {currentNode?.shortLabel ?? '—'}
            </span>
            <button
              type="button"
              className="lcv-dev-arr"
              onClick={next}
              disabled={currentIndex >= timeline.length - 1}
            >
              Next ▶
            </button>
          </div>

          <div className="lcv-dev-track">
            {timeline.map((node, index) => (
              <button
                key={node.id}
                type="button"
                className={[
                  'lcv-dev-dot',
                  index === currentIndex ? 'lcv-dev-dot-current' : '',
                  index < currentIndex ? 'lcv-dev-dot-past' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                title={node.label}
                onClick={() => onJump(node)}
              />
            ))}
          </div>

          <div className="lcv-dev-list">
            <div className="lcv-dev-group">
              <div className="lcv-dev-group-title">{lessonTitle}</div>
              <div className="lcv-dev-group-items">
                {timeline.map((node, index) => (
                  <button
                    key={node.id}
                    type="button"
                    className={[
                      'lcv-dev-node',
                      index === currentIndex ? 'lcv-dev-node-active' : '',
                      index < currentIndex ? 'lcv-dev-node-past' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => onJump(node)}
                  >
                    {node.shortLabel}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LCVDevStrip;
